import { Dispatch, SetStateAction, useCallback } from 'react';
import { AlertType, ButtonColor, ButtonVariant, TextButton } from '../../components';
import { useHandleApiResponse, useToast, useAppDispatch } from '../../hooks';
import { useDisconnectIntegrationMutation } from '../../services';
import { IntegrationConnectionStatus, IntegrationService } from '../../types';
import { updateOrganization } from '../../redux/reducers/authReducer';
import { capitalizeFirstLetter } from '../../utils';
import { INTEGRATION_SERVICE_TO_AUTH_KEY } from '../../constants';

type DisconnectButtonProps = {
  service: IntegrationService;
  setConnectionStatus: Dispatch<SetStateAction<IntegrationConnectionStatus>>;
};

const DisconnectButton = ({ service, setConnectionStatus }: DisconnectButtonProps) => {
  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();
  const dispatch = useAppDispatch();

  const [disconnectIntegration, { isLoading: isDisconnectingIntegration }] = useDisconnectIntegrationMutation();

  const handleDisconnect = useCallback(async () => {
    try {
      const response = await disconnectIntegration({ service });
      handleApiResponse({
        response,
        successMsg: `Successfully disconnected from ${capitalizeFirstLetter(service)}`,
        onSuccess: () => {
          dispatch(updateOrganization({ [INTEGRATION_SERVICE_TO_AUTH_KEY[service]]: undefined }));
          // To avoid the disconnect button flashing when the user disconnects
          setConnectionStatus(IntegrationConnectionStatus.DISCONNECTED);
        },
      });
    } catch (error) {
      showToast({ message: 'Something went wrong. Please try again later.', type: AlertType.ERROR });
      console.error('Failed to disconnect from Gong: ', error);
    }
  }, [disconnectIntegration, handleApiResponse, service, dispatch, showToast]);

  return (
    <TextButton
      text="Disconnect"
      color={ButtonColor.DESTRUCTIVE}
      variant={ButtonVariant.GHOST}
      onClick={handleDisconnect}
      loading={isDisconnectingIntegration}
    />
  );
};

export default DisconnectButton;
